import { Robot, Shield } from "@phosphor-icons/react";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Button, Tooltip } from "flowbite-react";
import { PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AnimatedModal from "../../../../components/AnimatedModal";
import { Table } from "../../../../components/Table";
import { useAuth } from "../../../../hooks";
import { useDeleteRole } from "../../../../hooks/delete/useDeleteRole";
import { useRoles } from "../../../../hooks/get/useRoles";
import { checkPermission } from "../../../../lib/utils";
import { PERMISSION_MAPPER } from "../../../../shared/constants";
import { IRole } from "../interface";
import { WarningCircle } from "@phosphor-icons/react";

const RoleManagement = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: roles = [], isLoading } = useRoles();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<IRole | null>(null);
  const deleteRole = useDeleteRole();


  const handleCreate = () => {
    navigate("/role-management/create");
  };

  const handleEdit = (role: IRole) => {
    navigate(`/role-management/${role.id}`);
  };

  const handleDelete = (role: IRole) => {
    setRoleToDelete(role);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (roleToDelete) {
      try {
        await deleteRole.mutateAsync(roleToDelete.id);
        setDeleteModalOpen(false);
        setRoleToDelete(null);
        toast.success(t("configurations.roles.deleteSuccess"));
      } catch {
        toast.error(t("configurations.roles.deleteError"));
        // Optionally handle error (e.g., show toast)
      }
    }
  };

  const hasRoleCreatePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_CREATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasRoleUpdatePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_UPDATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasRoleDeletePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_DELETE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const columns: ColumnDef<IRole>[] = [
    {
      accessorKey: "name",
      header: t("configurations.roles.name"),
      size: 140,
      cell: ({ row }) => {
        const permissions = row.original.permissions;
        const accountsEmpty = !permissions?.integration_pair || permissions.integration_pair.length === 0;
        const applicationsEmpty = !permissions?.application || permissions.application.length === 0;
        const showWarning = (accountsEmpty || applicationsEmpty) && row.original.created_by !== null;
        return (
          <div className="flex items-center gap-2">
            <span>{row.original.is_default && row.original.name.toLowerCase().trim() === "owner" ? "Account Owner" : row.original.name}</span>
            {showWarning && (
              <Tooltip content={t("configurations.roles.noDataShownForAssignedUser")}>
                <WarningCircle size={16} weight="fill" className="text-amber-500" />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "description",
      header: t("configurations.roles.description"),
      size: 180,
      cell: ({ row }) => (
        <div className="whitespace-pre-line break-words text-sm text-gray-500 dark:text-neutral-400">
          {row.original.description || "-"}
        </div>
      ),
    },
    {
      id: "created_by",
      header: t("configurations.roles.createdBy"),
      size: 150,
      cell: ({ row }) => {
        const createdBy = row.original.created_by;
        if (!createdBy)
          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center gap-2 h-8 w-8 rounded-full bg-gray-100 dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700">
                <Robot size={20} />
              </div>
              <div className="flex flex-col">
                <span>{"System"}</span>
              </div>
            </div>
          );
        return (
          <div className="flex items-center gap-2">
            {createdBy.profile_picture && (
              <img
                src={createdBy.profile_picture}
                alt={createdBy.name || createdBy.email || "User"}
                className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-neutral-700"
              />
            )}
            <div className="flex flex-col">
              <span>{createdBy.name || createdBy.email || "-"}</span>
              {createdBy.email && (
                <span className="text-xs text-gray-500">{createdBy.email}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: t("common.createdAt"),
      size: 120,
      cell: ({ row }) => {
        if (row.original.is_default) return "-";
        const createdAt = row.original.created_at || "";
        return createdAt ? format(new Date(createdAt), "dd MMM yyyy") : "-";
      },
    },
    {
      accessorKey: "updated_at",
      header: t("common.updatedAt"),
      size: 120,
      cell: ({ row }) => {
        if (row.original.is_default) return "-";
        const updatedAt = row.original.updated_at || "";
        return updatedAt ? format(new Date(updatedAt), "dd MMM yyyy") : "-";
      },
    },
    ...(hasRoleUpdatePermission || hasRoleDeletePermission
      ? [
        {
          id: "actions",
          header: t("common.actions"),
          size: 100,
          cell: ({ row }) => {
            if (!row.original.created_by) return null;
            return (
              <div className="flex items-center gap-2">
                {hasRoleUpdatePermission && (
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => handleEdit(row.original)}
                    className="!p-1"
                  >
                    <PencilSimple size={16} />
                  </Button>
                )}
                {hasRoleDeletePermission && (
                  <Button
                    size="xs"
                    color="failure"
                    onClick={() => handleDelete(row.original)}
                    className="!p-1"
                  >
                    <Trash size={16} />
                  </Button>
                )}
              </div>
            );
          },
        } as ColumnDef<IRole>,
      ]
      : []),
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="pl-2">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
            <h1 className="text-md font-bold text-gray-700 dark:text-gray-300">
              {t("configurations.roles.roles")}
            </h1>
          </div>
        </div>
        <div className="w-auto flex items-center gap-2">
          {hasRoleCreatePermission && (
            <Button color="indigo" size={"sm"} onClick={handleCreate}>
              <div className="flex items-center gap-2">
                <Plus size={16} />
                <span className="hidden sm:inline">{t("configurations.roles.create")}</span>
              </div>
            </Button>
          )}
        </div>
      </div>
      <Table
        data={roles}
        columns={columns}
        isLoading={isLoading}
        showPagination={true}
        showSearch={false}
        loadingHeight={4}
        loadingRowCount={5}
        maxHeight="h-full"
      />
      <AnimatedModal
        show={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        title={t("configurations.roles.deleteConfirm")}
        isConfirmation
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        confirmButtonColor="red"
        isConfirming={deleteRole.status === "pending"}
        onConfirm={confirmDelete}
      >
        <div className="text-center">
          <Trans
            i18nKey="configurations.roles.deleteConfirmMessage"
            values={{ role: roleToDelete?.name }}
            components={{ bold: <span className="font-bold text-red-500" /> }}
          />
        </div>
      </AnimatedModal>
    </div>
  );
};

export default RoleManagement;
